import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { MapPin, Search, Loader2 } from 'lucide-react';

const LIBRARIES = ['places'];
const MAP_CONTAINER = { width: '100%', height: '350px', borderRadius: '12px' };
const DEFAULT_CENTER = { lat: 10.4806, lng: -66.9036 };

const RestaurantConfigLocation = ({ data, onChange }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: LIBRARIES,
  });

  const [map, setMap] = useState(null);
  const autocompleteInput = useRef(null);
  const autocompleteRef = useRef(null);
  const sessionTokenRef = useRef(null);

  const center = data.coordinates?.latitude && data.coordinates?.longitude
    ? { lat: data.coordinates.latitude, lng: data.coordinates.longitude }
    : DEFAULT_CENTER;

  const extractLocationFromComponents = useCallback((address_components) => {
    let city = '';
    let country = '';
    let region = '';
    let comuna = '';
    if (!address_components) return { city, country, region, comuna };
    for (const comp of address_components) {
      if (comp.types.includes('locality')) city = comp.long_name;
      if (comp.types.includes('administrative_area_level_1')) region = comp.long_name;
      if (!city && comp.types.includes('administrative_area_level_1')) city = comp.long_name;
      if (comp.types.includes('country')) country = comp.long_name;
      if (!comuna && comp.types.includes('sublocality_level_1')) comuna = comp.long_name;
      if (!comuna && comp.types.includes('sublocality')) comuna = comp.long_name;
      if (!comuna && comp.types.includes('administrative_area_level_2')) comuna = comp.long_name;
    }
    return { city, country, region, comuna };
  }, []);

  const runReverseGeocode = useCallback((lat, lng) => {
    const google = window.google;
    if (!google?.maps) return;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) return;
      const place = results[0];
      const { city, country, region, comuna } = extractLocationFromComponents(place.address_components);
      onChange({
        address: place.formatted_address || '',
        city,
        country,
        region,
        comuna,
        coordinates: { latitude: lat, longitude: lng },
      });
    });
  }, [onChange, extractLocationFromComponents]);

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    mapInstance.addListener('click', (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      onChange(prev => ({
        ...prev,
        coordinates: { latitude: lat, longitude: lng },
      }));
      runReverseGeocode(lat, lng);
    });
  }, [onChange, runReverseGeocode]);

  useEffect(() => {
    if (!isLoaded || !autocompleteInput.current || autocompleteRef.current) return;

    const google = window.google;
    if (!google?.maps?.places) return;

    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();

    const autocomplete = new google.maps.places.Autocomplete(autocompleteInput.current, {
      types: ['establishment', 'geocode'],
      fields: ['formatted_address', 'geometry', 'address_components', 'name'],
      sessionToken: sessionTokenRef.current,
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry?.location) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const { city, country, region, comuna } = extractLocationFromComponents(place.address_components);

      onChange({
        address: place.formatted_address || place.name || '',
        city,
        country,
        region,
        comuna,
        coordinates: { latitude: lat, longitude: lng },
      });

      if (map) {
        map.panTo({ lat, lng });
        map.setZoom(16);
      }

      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    });

    autocompleteRef.current = autocomplete;
  }, [isLoaded, map, onChange, extractLocationFromComponents]);

  const handleMarkerDragEnd = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    onChange(prev => ({ ...prev, coordinates: { latitude: lat, longitude: lng } }));
    runReverseGeocode(lat, lng);
  }, [onChange, runReverseGeocode]);

  if (loadError) {
    return (
      <div className="text-center py-12">
        <MapPin size={32} className="mx-auto text-red-700 dark:text-red-400 mb-3" />
        <p className="text-red-700 dark:text-red-400 text-sm">Error al cargar Google Maps.</p>
        <p className="text-content-subtle text-xs mt-1">Verifica tu conexión e intenta de nuevo.</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-kumia-green" />
        <span className="ml-2 text-content-muted">Cargando Google Maps...</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Search */}
      <div>
        <label className="block text-sm font-medium text-content-secondary mb-1.5">
          <span className="flex items-center gap-1.5">
            <Search size={14} className="text-content-subtle" />
            Buscar dirección
          </span>
        </label>
        <input
          ref={autocompleteInput}
          type="text"
          placeholder="Escribe la dirección o nombre del restaurante..."
          defaultValue={data.address}
          className="w-full bg-surface-2 border border-line-strong rounded-lg px-3 py-2.5 text-content-primary text-sm focus:outline-none focus:border-kumia-green transition-colors placeholder:text-gray-600"
        />
      </div>

      {/* Map */}
      <div className="border border-line-subtle rounded-xl overflow-hidden">
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER}
          center={center}
          zoom={data.coordinates?.latitude ? 16 : 12}
          onLoad={onMapLoad}
          options={{
            styles: darkMapStyle,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          }}
        >
          {data.coordinates?.latitude !== 0 && (
            <Marker
              position={{ lat: data.coordinates.latitude, lng: data.coordinates.longitude }}
              draggable
              onDragEnd={handleMarkerDragEnd}
            />
          )}
        </GoogleMap>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1">Dirección</label>
          <input
            value={data.address}
            onChange={e => onChange({ ...data, address: e.target.value })}
            className="w-full bg-surface-2 border border-line-strong rounded-lg px-3 py-2 text-content-primary text-sm focus:outline-none focus:border-kumia-green transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1">Ciudad</label>
          <input
            value={data.city}
            onChange={e => onChange({ ...data, city: e.target.value })}
            className="w-full bg-surface-2 border border-line-strong rounded-lg px-3 py-2 text-content-primary text-sm focus:outline-none focus:border-kumia-green transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1">País</label>
          <input
            value={data.country ?? ''}
            onChange={e => onChange({ ...data, country: e.target.value })}
            className="w-full bg-surface-2 border border-line-strong rounded-lg px-3 py-2 text-content-primary text-sm focus:outline-none focus:border-kumia-green transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1">Región</label>
          <input
            value={data.region ?? ''}
            onChange={e => onChange({ ...data, region: e.target.value })}
            className="w-full bg-surface-2 border border-line-strong rounded-lg px-3 py-2 text-content-primary text-sm focus:outline-none focus:border-kumia-green transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1">Comuna</label>
          <input
            value={data.comuna ?? ''}
            onChange={e => onChange({ ...data, comuna: e.target.value })}
            className="w-full bg-surface-2 border border-line-strong rounded-lg px-3 py-2 text-content-primary text-sm focus:outline-none focus:border-kumia-green transition-colors"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-content-muted mb-1">Latitud</label>
            <input
              type="number"
              step="any"
              value={data.coordinates?.latitude || ''}
              onChange={e => onChange({
                ...data,
                coordinates: { ...data.coordinates, latitude: parseFloat(e.target.value) || 0 },
              })}
              className="w-full bg-surface-2 border border-line-strong rounded-lg px-3 py-2 text-content-primary text-sm focus:outline-none focus:border-kumia-green transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-content-muted mb-1">Longitud</label>
            <input
              type="number"
              step="any"
              value={data.coordinates?.longitude || ''}
              onChange={e => onChange({
                ...data,
                coordinates: { ...data.coordinates, longitude: parseFloat(e.target.value) || 0 },
              })}
              className="w-full bg-surface-2 border border-line-strong rounded-lg px-3 py-2 text-content-primary text-sm focus:outline-none focus:border-kumia-green transition-colors"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const darkMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#181818' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1a3a1a' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f2f2f' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
];

export default RestaurantConfigLocation;
